import React, { useCallback, useMemo } from 'react';
import { camelCase, capitalize, isFinite, startCase } from 'lodash';
import { forwardStockedStatusLabel } from '@/utilities/forwardStockedStatus';
import STATES_INVERTED from '@/constants/statesInverted';
import { asBrowserDate, asUTCDate } from '@/utilities/convertToISO';
import { format, differenceInDays, addDays, subBusinessDays, isToday } from 'date-fns';
import { customerTimeframe, delWindowToFormattedDateRange } from '@/utilities/delWindowToFormattedDate';
import { calcMilestone, EXCEPTION_DISPLAY } from '@onward-delivery/core';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@material-ui/icons/Close';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import {
    PRE_TRANSIT_ORDER_STATUSES,
    IN_TRANSIT_ORDER_STATUSES,
    COMPLETE_ORDER_STATUSES,
    CONFIRMED_ORDER_STATUSES,
    UNCONFIRMED_ORDER_STATUSES,
} from '@onward-delivery/core';
import { css } from '@emotion/react';
import { aggregateEvents } from '@onward-delivery/core';
import { colors } from '@/styles';
import { RECEIVED_STATUSES } from '@/constants/manifestStatuses';
import { BodyCellText } from '@/components/MyOrders/blocks';
import { toNational } from '@/utilities/formatPhoneNumber';
import { MARKETPLACE_TIER_DISPLAYS } from '@/components/admin/AdminPricing/constants';
import { getMilestoneStyles } from '@/utilities/milestoneStyles';
import { findMatchingWarehouseEvent, getWarehouseEstimates } from '@/utilities/warehouseEvents';
import { dispatch_integration_types } from '@/components/MyOrders/filterOptions';

const date = (timestamp) => {
    return timestamp ? format(asBrowserDate(timestamp), 'MM/dd/yyyy') : '-';
};

const dwell = (pickup, completion) => {
    return pickup && completion ? `${differenceInDays(new Date(completion), new Date(pickup))} days` : '-';
};

const dateShort = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});

const timestamp = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
});

const INTEGRATION_TYPES_BY_KEY = Object.fromEntries(dispatch_integration_types.map((t) => [t.value, t.label]));

const rateFormat = (rate) => {
    if (isFinite(rate)) {
        return `$${rate.toFixed(2)}`;
    }
    return '-';
};

const marginFormat = (margin) => {
    if (isFinite(margin)) {
        return `${margin.toFixed(2)} %`;
    }
    return '-';
};

const getOrderKind = (order) => {
    const events = order?.wh_events || [];
    const hasAwaitingOrReceiving = events.some((e) => {
        return e.action === 'START:AWAITING' || e.action === 'START:RECEIVING';
    });

    if (order?.priced_as_middle_mile && order?.has_middle_mile_origin) return 'MM + Delivery';
    if (hasAwaitingOrReceiving) return 'Receiving + Delivery';
    return 'Pickup + Delivery';
};

export const useTabs = ({ page = 'admin' }) => {
    return useMemo(() => {
        const TODAY = new Date(new Date().setHours(0, 0, 0, 0));

        return [
            {
                value: 'DISTRESSED_SHIPMENTS',
                label: 'Distressed Shipments',
                restrictedView: 'admin',
                filters: [
                    {
                        order_status: {
                            _in: [
                                'pending',
                                'open',
                                'claimed',
                                'inProgress',
                                'routedInProgress',
                                'on_hold',
                                'rescheduled',
                            ],
                        },
                    },
                ],
            },
            {
                value: 'PICKUPS',
                label: 'Pickups',
                filters: [
                    { order_status: { _nin: [...UNCONFIRMED_ORDER_STATUSES, 'deleted', 'incomplete', 'cancelled'] } },
                    { order_type: { _in: ['delivery', 'exchange', 'service'] } },
                    {
                        _not: {
                            wh_events: {
                                action: { _in: ['START:AWAITING'] },
                            },
                        },
                    },
                ],
            },
            {
                value: 'INBOUND',
                label: 'Inbound',
                filters: [
                    { order_status: { _nin: [...UNCONFIRMED_ORDER_STATUSES, 'deleted', 'incomplete', 'cancelled'] } },
                    {
                        _and: [
                            {
                                wh_events: {
                                    action: { _eq: 'START:AWAITING' },
                                },
                            },
                            {
                                _not: {
                                    wh_events: {
                                        status: { _eq: 'RECEIVED' },
                                    },
                                },
                            },
                        ],
                    },
                ],
            },
            {
                value: 'INCOMPLETE',
                label: 'Incomplete',
                filters: [
                    {
                        _or: [
                            { _and: [{ source_form: { _neq: 'APIv2' } }, { order_status: { _in: ['duplicate'] } }] },
                            { order_status: { _in: ['incomplete', 'pending_confirmation', 'processing'] } },
                        ],
                    },
                    { job_id: { _is_null: false } },
                ],
            },
            {
                value: 'ALL',
                label: 'All',
                filters: [{ order_status: { _nin: [...UNCONFIRMED_ORDER_STATUSES, 'deleted', 'incomplete'] } }],
            },
            {
                value: 'ALL_ACTIVE',
                label: 'All - Active',
                filters: [{ order_status: { _in: ['pending', 'open', 'claimed', 'inProgress', 'routedInProgress'] } }],
            },
            {
                value: 'UNSCHEDULED',
                label: 'Unscheduled',
                filters: [
                    { order_status: { _in: ['pending', 'claimed'] } },
                    { _not: { routes: {} } },
                    { carrier_id: { _is_null: false } },
                    { delivery_date: { _is_null: true } },
                ],
            },
            {
                value: 'SCHEDULED',
                label: 'Scheduled',
                filters: [
                    { order_status: { _in: ['pending', 'claimed'] } },
                    { carrier_id: { _is_null: false } },
                    { delivery_date: { _is_null: false } },
                    { pickup_date: { _is_null: false } },
                    { _or: [{ del_window_start: { _is_null: true } }, { del_window_end: { _is_null: true } }] },
                    {
                        wh_events: { transition: { next: { _eq: 'RECEIVING' } } },
                    },
                ],
            },
            {
                value: 'OUT_FOR_DELIVERY',
                label: 'Out for Delivery',
                filters: [
                    {
                        order_status: {
                            _nin: [
                                ...COMPLETE_ORDER_STATUSES,
                                ...UNCONFIRMED_ORDER_STATUSES,
                                'deleted',
                                'incomplete',
                                'cancelled',
                                'rescheduled',
                            ],
                        },
                    },
                    {
                        _or: [
                            { order_status: { _in: IN_TRANSIT_ORDER_STATUSES } },
                            {
                                _and: [
                                    { delivery_date: { _is_null: false } },
                                    { delivery_date: { _gte: TODAY.toISOString() } },
                                    { delivery_date: { _lt: addDays(TODAY, 1).toISOString() } },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                value: 'MISSING_POD',
                label: 'Missing POD/POP',
                filters: [
                    { order_status: { _in: COMPLETE_ORDER_STATUSES } },
                    { customer_signature: { _is_null: true } },
                    { _or: [{ pod: { _is_null: true } }, { pod: { _eq: '' } }] },
                ],
                red: true,
            },
            {
                value: 'COMPLETED',
                label: 'Completed',
                filters: [
                    { order_status: { _in: COMPLETE_ORDER_STATUSES } },
                    {
                        _or: [
                            { customer_signature: { _is_null: false } },
                            { pod: { _is_null: false } },
                            { pod: { _neq: '' } },
                        ],
                    },
                ],
            },
        ].filter((tab) => !tab?.restrictedView || tab?.restrictedView === page);
    }, []);
};

export const useRowHighlight = (tab) => {
    return useCallback(
        (row) => {
            const order = row.original;
            const NOW = new Date();

            switch (tab.value) {
                case 'CLAIMED':
                    if (addDays(new Date(order.claimed_date), 7).getTime() < NOW.getTime()) {
                        row.red = true;
                    }
                    break;
                case 'RECEIVED': {
                    const receive = findMatchingWarehouseEvent(order.wh_events, { action: 'START:AWAITING' });
                    if (addDays(new Date(receive.created_at), 1).getTime() < NOW.getTime()) {
                        row.red = true;
                    }
                    break;
                }
                case 'SCHEDULED': {
                    if (!order?.delivery_date) {
                        break;
                    }
                    const deliveryDate = new Date(order.delivery_date);
                    const previousBusinessDay = subBusinessDays(deliveryDate, 1);
                    if (isToday(previousBusinessDay) && !order.del_window_start && !order.del_window_end) {
                        row.red = true;
                    }
                    break;
                }
                case 'DNF':
                case 'DELIVERED':
                    if (addDays(new Date(order.delivery_date), 2).getTime() < NOW.getTime()) {
                        row.red = true;
                    }
                    break;
                case 'EXCEPTION_REPORTED':
                    if (
                        [
                            ...(order?.exceptions || []).filter((mapping) => mapping.exception.status !== 'RESOLVED'),
                            ...(order?.itemsByOrderId || []).reduce((acc, item) => {
                                acc = [
                                    ...acc,
                                    ...(item?.exceptions || []).filter(
                                        (mapping) => mapping.exception.status !== 'RESOLVED'
                                    ),
                                ];
                                return acc;
                            }, []),
                        ].some((mapping) => {
                            return addDays(new Date(mapping.created_at), 1).getTime() < NOW.getTime();
                        })
                    ) {
                        row.red = true;
                    }
                    break;
            }
        },
        [tab]
    );
};

export const useColumns = (tab, callbacks = {}, { onwardAdminsById } = {}) => {
    return useMemo(() => {
        return [
            {
                Header: '',
                id: 'order-details-group',
                columns: [
                    {
                        id: 'order_number',
                        Header: 'Order',
                        accessor: (order) => order.order_number,
                        disableSortBy: false,
                        width: 125,
                    },
                    {
                        id: 'po_number',
                        Header: 'PO #',
                        accessor: (order) => order.po_number || '-',
                        disableSortBy: false,
                        width: 150,
                    },
                    {
                        id: 'route',
                        Header: 'Route',
                        accessor: (order) => {
                            const pickup = order.routes.find((mapping) => mapping.type === 'PICKUP')?.route;
                            const dropoff = order.routes.find((mapping) => mapping.type !== 'PICKUP')?.route;
                            return [...(pickup ? [pickup?.route_number] : []), dropoff?.route_number || '-'].join(
                                ' / '
                            );
                        },
                        disableSortBy: true,
                        width: 110,
                    },
                    {
                        id: 'shipper',
                        Header: 'Shipper',
                        accessor: (order) => order.order_shipper?.business_name || '-',
                        disableSortBy: false,
                        width: 200,
                    },
                    {
                        id: 'carrier',
                        Header: 'Carrier',
                        accessor: (order) => order.order_carrier?.business_name || '-',
                        disableSortBy: false,
                        width: 200,
                    },
                    {
                        id: 'order_program',
                        Header: 'Order Program',
                        accessor: (order) => order.program?.display_name || '-',
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'assigned_admin',
                        Header: 'Assigned Admin',
                        accessor: (order) => {
                            const adminId = order?.assigned_admin_id;
                            if (!adminId) return '-';
                            return onwardAdminsById?.[adminId] || adminId;
                        },
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'tier',
                        Header: 'Tier',
                        accessor: (order) => {
                            const tier = order.shipper_rate_override?.marketplace_tier;
                            return tier ? MARKETPLACE_TIER_DISPLAYS[tier] : 'UNKNOWN';
                        },
                        disableSortBy: true,
                        width: 175,
                    },
                    {
                        id: 'reference_id',
                        Header: 'Reference #',
                        accessor: (order) => {
                            return order.reference_id || '-';
                        },
                        disableSortBy: false,
                        width: 175,
                    },
                    {
                        id: 'customer',
                        Header: 'Customer',
                        accessor: (order) => order.dropoff_name || '-',
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'customer_email',
                        Header: 'Customer Email',
                        accessor: (order) => order.dropoff_email || '-',
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'customer_phone',
                        Header: 'Customer Phone',
                        accessor: (order) => toNational(order.dropoff_phone) || '-',
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'order_type',
                        Header: 'Type',
                        accessor: (order) => capitalize(order.order_type),
                        disableSortBy: true,
                        width: 100,
                    },
                    {
                        id: 'order_kind',
                        Header: 'Order Kind',
                        accessor: (order) => getOrderKind(order),
                        disableSortBy: true,
                        width: 175,
                    },
                    ...(tab === 'OPEN'
                        ? [
                              {
                                  id: 'bids',
                                  Header: '# Quotes',
                                  accessor: (order) => order?.listing?.bids?.length,
                                  disableSortBy: true,
                                  width: 100,
                              },
                          ]
                        : []),
                    {
                        id: 'origin',
                        Header: 'Origin',
                        accessor: (order) =>
                            `${capitalize(order.pickup_city)}, ${
                                STATES_INVERTED[startCase(camelCase(order.pickup_state))] ||
                                order.pickup_state?.toUpperCase()
                            }`,
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'destination',
                        Header: 'Destination',
                        accessor: (order) =>
                            `${capitalize(order.dropoff_city)}, ${
                                STATES_INVERTED[startCase(camelCase(order.dropoff_state))] ||
                                order.dropoff_state?.toUpperCase()
                            }`,
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'posted_date',
                        Header: 'Posted',
                        accessor: (order) => date(order.posted_date || order.created_at),
                        disableSortBy: false,
                        width: 150,
                    },
                    {
                        id: 'last_modified_at',
                        Header: 'Last Updated',
                        accessor: (order) => date(order.last_modified_at),
                        width: 150,
                    },
                    {
                        id: 'delivery_date_scheduled_at',
                        Header: 'Scheduled On',
                        accessor: (order) => date(order.delivery_date_scheduled_at),
                        width: 175,
                    },
                    {
                        id: 'claimed_date',
                        Header: 'Claimed',
                        accessor: (order) => date(order.claimed_date),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'first_available_date',
                        Header: 'Available',
                        accessor: (order) => date(order.first_available_date),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'pickup_date',
                        Header: 'Pickup',
                        accessor: (order) => date(order.pickup_date),
                        disableSortBy: true,
                        width: 125,
                    },
                    {
                        id: 'delivery_date',
                        Header: 'Delivery',
                        accessor: (order) => date(order.delivery_date),
                        width: 125,
                    },
                    {
                        id: 'timeframe',
                        Header: 'Timeframe',
                        accessor: (order) =>
                            customerTimeframe(
                                order.del_window_start,
                                order.del_window_end,
                                order.order_type === 'return' ? order.pickup_zip : order.dropoff_zip
                            ) || '-',
                        disableSortBy: true,
                        width: 250,
                    },
                    {
                        id: 'delivery_type',
                        Header: 'Delivery Type',
                        accessor: (order) =>
                            LOCATION_TYPES[order.dropoff_location_type] || startCase(order.dropoff_location_type),
                        width: 175,
                    },
                    {
                        id: 'cubes',
                        Header: 'Cubes',
                        accessor: (order) =>
                            (order.itemsByOrderId || [])
                                .reduce((acc, item) => {
                                    return acc + item.total_cubes || 0;
                                }, 0)
                                .toFixed(0),
                        disableSortBy: true,
                        width: 120,
                    },
                    {
                        id: 'weight',
                        Header: 'Weight',
                        accessor: (order) =>
                            (order.itemsByOrderId || [])
                                .reduce((acc, item) => {
                                    return acc + item.total_weight || 0;
                                }, 0)
                                .toFixed(0),
                        disableSortBy: true,
                        width: 120,
                    },
                    {
                        id: 'pieces',
                        Header: 'Pieces',
                        accessor: (order) =>
                            (order.itemsByOrderId || []).reduce((acc, item) => {
                                const qty = Number(item?.quantity);
                                return acc + (Number.isFinite(qty) ? qty : 0);
                            }, 0),
                        disableSortBy: true,
                        width: 120,
                    },
                    {
                        id: 'shipper_rate',
                        Header: 'Shipper Rate',
                        accessor: (order) => order.shipper_rate,
                        Cell: ({ value }) => rateFormat(value),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'middle_mile_shipper_rate',
                        Header: 'MM Shipper Rate',
                        accessor: (order) => order.middle_mile_shipper_rate,
                        Cell: ({ value }) => rateFormat(value),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'carrier_rate',
                        Header: 'Carrier Rate',
                        accessor: (order) => order.carrier_rate,
                        Cell: ({ value }) => rateFormat(value),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'middle_mile_carrier_rate',
                        Header: 'MM Carrier Rate',
                        accessor: (order) => order.middle_mile_carrier_rate,
                        Cell: ({ value }) => rateFormat(value),
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'margin',
                        Header: 'Margin',
                        accessor: (order) => {
                            const shipperTotal = (order.shipper_rate || 0) + (order.middle_mile_shipper_rate || 0);
                            const carrierTotal = (order.carrier_rate || 0) + (order.middle_mile_carrier_rate || 0);
                            return (100 * (shipperTotal - carrierTotal)) / shipperTotal;
                        },
                        Cell: ({ value }) => marginFormat(value),
                        disableSortBy: true,
                        width: 120,
                    },
                    {
                        id: 'manufacturer',
                        Header: 'Manufacturer',
                        accessor: (order) => order?.manufacturer || '-',
                        disableSortBy: true,
                        width: 150,
                    },
                    {
                        id: 'middle_mile_integration_status',
                        Header: 'MM Status',
                        width: 200,
                        accessor: (order) => {
                            return order.middle_mile_integration_status;
                        },
                        Cell: ({ row: order, value }) => {
                            return value ? startCase(value.toLowerCase()).replace('_', '-') : '-';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'forward_stocked_status',
                        Header: 'Forward Stocked Status',
                        width: 200,
                        accessor: (order) => forwardStockedStatusLabel(order.forward_stocked_status),
                        disableSortBy: true,
                    },
                    {
                        id: 'dispatch_integration_type',
                        Header: 'Agent TMS',
                        accessor: (order) => {
                            return INTEGRATION_TYPES_BY_KEY[order.dispatch_integration_type] || '-';
                        },
                        Cell: ({ value }) => value || '-',
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'dispatch_integration_meta.tracking_number',
                        Header: 'Agent TMS #',
                        width: 175,
                        accessor: (order) => {
                            return order.dispatch_integration_meta?.tracking_number;
                        },
                        Cell: ({ value }) => value || '-',
                        disableSortBy: true,
                    },
                    {
                        id: 'middle_mile_integration_meta.tracking_number',
                        Header: 'MM Tracking #',
                        width: 200,
                        accessor: (order) => {
                            return order.middle_mile_integration_meta?.tracking_number;
                        },
                        Cell: ({ row: order, value }) => {
                            return value || '-';
                        },
                        disableSortBy: true,
                    },
                    {
                        id: 'middle_mile_integration_type',
                        Header: 'MM Integration',
                        width: 200,
                        accessor: (order) => order.middle_mile_integration_type,
                        Cell: ({ value }) => value || '-',
                        disableSortBy: true,
                    },
                    {
                        id: 'middle_mile_integration_meta.carrier_name',
                        Header: 'MM Carrier',
                        width: 200,
                        accessor: (order) => order.middle_mile_integration_meta?.carrier_name,
                        Cell: ({ value }) => value || '-',
                        disableSortBy: true,
                    },
                    {
                        id: 'warehousestatus',
                        Header: 'Warehousing Status',
                        accessor: (order) => {
                            const receive = findMatchingWarehouseEvent(order.wh_events, {
                                transitionNext: 'RECEIVING',
                            });
                            return receive?.status || 'NOT_DELIVERED';
                        },
                        Cell: ({ row, value }) => {
                            let fragment;
                            let label;
                            let icon = null;

                            const order = row.original;
                            switch (value) {
                                case 'RECEIVED': {
                                    label = 'Received';
                                    fragment = css`
                                        color: ${colors.greens.primary};
                                    `;
                                    break;
                                }
                                case 'NOT_DELIVERED':
                                default: {
                                    const [received, total] = (order.itemsByOrderId || []).reduce(
                                        ([recAcc, totAcc], item) => {
                                            return [
                                                recAcc +
                                                    (RECEIVED_STATUSES.includes(item.pallet?.warehouse_status)
                                                        ? item.quantity || 1
                                                        : 0),
                                                totAcc + (item.quantity || 1),
                                            ];
                                        },
                                        [0, 0]
                                    );

                                    if (received > 0) {
                                        label = `${received}/${total} Received`;
                                        fragment = css`
                                            color: ${colors.blues[0]};
                                        `;
                                    } else {
                                        label = 'Not Received';
                                        fragment = css`
                                            color: ${colors.greys[3]};
                                        `;
                                    }
                                    break;
                                }
                            }

                            return (
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        white-space: nowrap;
                                    `}
                                >
                                    <BodyCellText
                                        css={css`
                                            font-weight: 700;
                                            ${fragment};
                                        `}
                                    >
                                        {label}
                                    </BodyCellText>
                                    {icon}
                                </div>
                            );
                        },
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'rereceivingstatus',
                        Header: 'Rereceiving Status',
                        accessor: (order) => {
                            const warehouse = aggregateEvents(order.wh_events);
                            return warehouse.status;
                        },
                        Cell: ({ row, value }) => {
                            let fragment;
                            let label;
                            let icon = null;
                            switch (value) {
                                case 'RECEIVED': {
                                    label = 'Received';
                                    fragment = css`
                                        color: ${colors.greens.primary};
                                    `;
                                    break;
                                }
                                case 'NOT_DELIVERED':
                                default: {
                                    label = 'Not Received';
                                    fragment = css`
                                        color: ${colors.greys[3]};
                                    `;

                                    break;
                                }
                            }

                            return (
                                <div
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        white-space: nowrap;
                                    `}
                                >
                                    <BodyCellText
                                        css={css`
                                            font-weight: 700;
                                            ${fragment};
                                        `}
                                    >
                                        {label}
                                    </BodyCellText>
                                    {icon}
                                </div>
                            );
                        },
                        disableSortBy: true,
                        width: 200,
                    },
                    {
                        id: 'est_ship_date',
                        Header: 'Estimated Ship Date',
                        accessor: (order) => {
                            const { est_ship_date } = getWarehouseEstimates(order);
                            return est_ship_date ? new Date(est_ship_date) : null;
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'est_received_date',
                        Header: 'Estimated Received Date',
                        accessor: (order) => {
                            const { est_received_date } = getWarehouseEstimates(order);
                            return est_received_date ? new Date(est_received_date) : null;
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    {
                        id: 'received_date',
                        Header: 'Actual Received Date',
                        accessor: (order) => {
                            const receive = findMatchingWarehouseEvent(order.wh_events, {
                                transitionNext: 'RECEIVING',
                            });
                            return new Date(receive.received_date);
                        },
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) {
                                return '-';
                            }

                            return dateShort.format(value);
                        },
                        width: 200,
                    },
                    ...(['DNF', 'DELIVERED', 'COMPLETED'].includes(tab)
                        ? [
                              {
                                  Header: 'POD',
                                  id: 'pod',
                                  width: 110,
                                  disableSortBy: false,
                                  Cell: ({ row }) => {
                                      const order = row.original;
                                      const hasPhoto = order.delivery_photo?.length >= 1 || order.pod?.length >= 1;
                                      const hasSignature = order.customer_signature?.length >= 1;

                                      return hasSignature || hasPhoto ? (
                                          <>
                                              <DoneIcon
                                                  css={css`
                                                      color: ${colors.greens.primary};
                                                  `}
                                              />
                                              <button
                                                  onClick={(e) => {
                                                      e.stopPropagation();
                                                      callbacks.viewOrderPod(order);
                                                  }}
                                                  css={css`
                                                      color: ${colors.greens.primary};
                                                      text-decoration: underline;
                                                      margin: 5px;
                                                      font-weight: 500;
                                                      background: none;
                                                      border: none;
                                                      cursor: pointer;
                                                  `}
                                              >
                                                  View
                                              </button>
                                          </>
                                      ) : (
                                          <CloseIcon
                                              css={css`
                                                  color: ${colors.reds.primary};
                                                  background: none;
                                              `}
                                          />
                                      );
                                  },
                              },
                          ]
                        : []),
                    ...(tab === 'EXCEPTION_REPORTED'
                        ? [
                              {
                                  id: 'exception_types',
                                  Header: 'Exception Types',
                                  disableSortBy: true,
                                  accessor: (order) => {
                                      const exceptions = [
                                          ...(order?.order_exception
                                              ? [EXCEPTION_DISPLAY[order?.order_exception.type]]
                                              : []),
                                          ...order.itemsByOrderId
                                              .filter((item) => !!item.item_exception)
                                              .map((item) => EXCEPTION_DISPLAY[item?.item_exception?.type]),
                                      ];

                                      return Array.from(new Set(exceptions)).join(' / ');
                                  },
                              },
                              {
                                  id: 'exception_reported_at',
                                  Header: 'Reported At',
                                  disableSortBy: true,
                                  accessor: (order) => {
                                      const exceptions = [
                                          ...(order?.order_exception ? [order?.order_exception.reported_at] : []),
                                          ...order.itemsByOrderId
                                              .filter((item) => !!item.item_exception)
                                              .map((item) => item?.item_exception?.reported_at),
                                      ];

                                      return Array.from(new Set(exceptions)).join(' / ');
                                  },
                              },
                              {
                                  id: 'exception_created_at',
                                  Header: 'Exception Time',
                                  disableSortBy: true,
                                  accessor: (order) => {
                                      const exceptions = [
                                          ...(order?.order_exception
                                              ? [new Date(order?.order_exception.created_at).getTime()]
                                              : []),
                                          ...order.itemsByOrderId
                                              .filter((item) => !!item.item_exception)
                                              .map((item) => new Date(item?.item_exception?.created_at).getTime()),
                                      ].filter((timestamp) => !isNaN(timestamp) && isFinite(timestamp));

                                      return exceptions.length > 0 ? Math.max(...exceptions) : null;
                                  },
                                  Cell: ({ value }) => {
                                      if (!value || isNaN(value) || !isFinite(value)) {
                                          return '-';
                                      }
                                      const date = new Date(value);
                                      if (isNaN(date.getTime())) {
                                          return '-';
                                      }
                                      return timestamp.format(date);
                                  },
                              },
                          ]
                        : []),
                    {
                        id: 'loaded_at',
                        Header: 'Loaded At',
                        accessor: (order) => new Date(order?.loading_completed),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return timestamp.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'ofd_at',
                        Header: 'OFD At',
                        accessor: (order) => new Date(order?.ltl_pickup_completed),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return timestamp.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'arrived_at',
                        Header: 'Arrived At',
                        accessor: (order) => new Date(order?.ltl_dropoff_arrival),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return timestamp.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'completed_at',
                        Header: 'Completed At',
                        accessor: (order) => new Date(order?.completion_time),
                        Cell: ({ value }) => {
                            if (isNaN(value) || !value?.getTime()) return '-';
                            return timestamp.format(value);
                        },
                        width: 200,
                        disableSortBy: true,
                    },
                    {
                        id: 'photos_present',
                        Header: 'Photos',
                        accessor: (order) => order?.delivery_photo,
                        Cell: ({ value }) => {
                            const hasAny =
                                (Array.isArray(value) && value.length > 0) ||
                                (typeof value === 'string' && value.trim().length > 0) ||
                                (!!value && typeof value === 'object' && Object.keys(value).length > 0);
                            return hasAny ? (
                                <DoneIcon
                                    css={css`
                                        color: ${colors.greens.primary};
                                    `}
                                />
                            ) : (
                                '-'
                            );
                        },
                        width: 110,
                        disableSortBy: true,
                    },
                ],
            },
            {
                sticky: 'right',
                Header: '',
                id: 'sticky-right',
                columns: [
                    {
                        id: 'milestone',
                        Header: 'Status',
                        accessor: (order) => calcMilestone(order) || '-',
                        width: 200,
                        disableSortBy: true,
                        Cell: ({ value }) => {
                            if (!value || value === '-') {
                                return value;
                            }
                            return <span css={getMilestoneStyles(value)}>{value}</span>;
                        },
                    },
                    {
                        id: 'show_hidden_columns',
                        Header: '',
                        accessor: () => '',
                        width: 52,
                        minWidth: 52,
                        maxWidth: 52,
                        disableSortBy: true,
                        disableResizing: true,
                    },
                ],
            },
        ];
    }, [tab, callbacks, onwardAdminsById]);
};

export const ACCOUNTING_COLUMNS = [
    {
        id: 'order_number',
        Header: 'Order',
        accessor: (order) => order.order_number,
        width: 100,
    },
    {
        id: 'po_number',
        Header: 'PO #',
        accessor: (order) => order.po_number || '-',
        disableSortBy: false,
        width: 150,
    },
    {
        id: 'order_type',
        Header: 'Type',
        accessor: (order) => capitalize(order.order_type),
        disableSortBy: true,
        width: 100,
    },
    {
        id: 'shipper',
        Header: 'Shipper',
        accessor: (order) => order.order_shipper?.business_name || '-',
        disableSortBy: true,
        width: 250,
    },
    {
        id: 'carrier',
        Header: 'Carrier',
        accessor: (order) => order.order_carrier?.business_name || '-',
        disableSortBy: true,
        width: 250,
    },
    {
        id: 'carrier_email',
        Header: 'Carrier Email',
        accessor: (order) => order.order_carrier?.email || '-',
        disableSortBy: true,
        width: 250,
    },
    {
        id: 'carrier_phone',
        Header: 'Carrier Phone',
        accessor: (order) => order.order_carrier?.business_phone || '-',
        disableSortBy: true,
        width: 250,
    },
    {
        id: 'customer',
        Header: 'Customer',
        accessor: (order) => order.dropoff_name || '-',
        disableSortBy: true,
        width: 150,
    },
    {
        id: 'shipper_rate',
        Header: 'Shipper Rate',
        accessor: (order) => order.shipper_rate,
        Cell: ({ value }) => rateFormat(value),
        disableSortBy: true,
        width: 125,
    },
    {
        id: 'carrier_rate',
        Header: 'Carrier Rate',
        accessor: (order) => order.carrier_rate,
        Cell: ({ value }) => rateFormat(value),
        disableSortBy: true,
        width: 125,
    },
    {
        id: 'margin',
        Header: 'Margin',
        accessor: (order) => (100 * (order.shipper_rate - order.carrier_rate)) / order.shipper_rate,
        Cell: ({ value }) => marginFormat(value),
        disableSortBy: true,
        width: 100,
    },
    {
        id: 'payment_type',
        Header: 'Payment Type',
        accessor: (order) => `${order.listing?.payment_intent_id ? 'Credit Card' : 'Invoice'}`,
        disableSortBy: true,
        width: 150,
    },
    {
        id: 'delivery_type',
        Header: 'Delivery Type',
        accessor: (order) => LOCATION_TYPES[order.dropoff_location_type] || startCase(order.dropoff_location_type),
        width: 150,
    },
    {
        id: 'origin',
        Header: 'Origin',
        accessor: (order) =>
            `${capitalize(order.pickup_city)}, ${
                STATES_INVERTED[startCase(camelCase(order.pickup_state))] || order.pickup_state?.toUpperCase()
            }`,
        disableSortBy: true,
        width: 150,
    },
    {
        id: 'destination',
        Header: 'Destination',
        accessor: (order) =>
            `${capitalize(order.dropoff_city)}, ${
                STATES_INVERTED[startCase(camelCase(order.dropoff_state))] || order.dropoff_state?.toUpperCase()
            }`,
        disableSortBy: true,
        width: 150,
    },
    {
        id: 'pickup_date',
        Header: 'Pickup',
        accessor: (order) => date(order.pickup_date),
        disableSortBy: true,
        width: 100,
    },
    {
        id: 'delivery_date',
        Header: 'Scheduled Delivery',
        accessor: (order) => date(order.delivery_date),
        disableSortBy: false,
        width: 150,
    },
    {
        id: 'completed_date',
        Header: 'Actual Completed',
        accessor: (order) => date(order.completion_time),
        disableSortBy: true,
        width: 150,
    },
    {
        id: 'dwell_time',
        Header: 'Dwell Time',
        accessor: (order) => dwell(order.pickup_date, order.completion_time || order.delivery_date),
        disableSortBy: true,
        width: 150,
    },
];

export const SEARCH_OPTIONS = [
    {
        label: 'Order Number',
        value: 'order_number',
    },
    {
        label: 'PO Number',
        value: 'po_number',
    },
    {
        label: 'Reference ID',
        value: 'reference_id',
    },
    {
        label: 'Customer Name',
        value: 'dropoff_name',
    },
    {
        label: 'Customer Email',
        value: 'dropoff_email',
    },
    {
        label: 'Customer Phone',
        value: 'dropoff_phone',
        parser: (value) => {
            return value.replace(/\D/g, '');
        },
    },
];
